import React from "react";
import { X, TrendingDown, Wallet } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";

const PriceComparisonDialog = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const g = t.price_comparison;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="price-comparison-dialog"
        className="bg-[#0A0A0A] border border-[#00A651]/30 text-white rounded-none max-w-2xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <div className="relative p-8 md:p-10">
          <DialogClose
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center border border-white/15 hover:border-[#00A651] hover:text-[#00A651] transition-colors z-10"
            data-testid="price-comparison-close"
            aria-label={t.close}
          >
            <X className="w-4 h-4" />
          </DialogClose>

          <div className="inline-flex items-center gap-2 bg-[#00A651] text-black font-condensed uppercase tracking-widest text-[10px] px-2.5 py-1 mb-5">
            <Wallet className="w-3 h-3" />
            Value
          </div>

          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="font-brush text-4xl md:text-5xl text-[#00A651] leading-tight uppercase">
              {g.title}
            </DialogTitle>
            <DialogDescription className="text-white/60 font-condensed uppercase tracking-widest text-xs">
              {g.subtitle}
            </DialogDescription>
          </DialogHeader>

          <p className="mt-6 text-white/80 text-base leading-relaxed">{g.intro}</p>

          <div className="mt-6 border border-white/10 overflow-hidden">
            <table className="w-full text-left" data-testid="price-comparison-table">
              <thead>
                <tr className="bg-black/60 border-b border-white/10">
                  {g.columns.map((c, i) => (
                    <th
                      key={i}
                      className="font-condensed uppercase tracking-widest text-[11px] text-white/50 px-4 py-3"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {g.rows.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-white/5 hover:bg-black/40 transition-colors"
                    data-testid={`price-row-${i}`}
                  >
                    <td className="px-4 py-3 text-white/80">{row.segment}</td>
                    <td className="px-4 py-3 text-white/70 font-condensed tracking-wide">{row.price}</td>
                    <td className="px-4 py-3 text-red-400/80 font-condensed text-sm">{row.diff}</td>
                  </tr>
                ))}
                <tr className="bg-white/5 border-b border-white/10" data-testid="price-row-average">
                  <td className="px-4 py-3 text-white/80 font-condensed uppercase tracking-wide text-xs">
                    {g.average_label}
                  </td>
                  <td className="px-4 py-3 text-white font-display text-xl leading-none">
                    {g.average_price}
                  </td>
                  <td className="px-4 py-3 text-red-400 font-condensed text-sm">+ CHF 61</td>
                </tr>
                <tr className="bg-[#00A651]/10 border-t-2 border-[#00A651]" data-testid="price-row-ours">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-[#00A651]" />
                      <span className="font-brush text-xl text-white leading-none">
                        {g.ours_label}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="font-display text-2xl text-[#00A651] leading-none">
                      {g.ours_price}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-[#00A651] font-condensed uppercase tracking-widest text-xs">
                    ↓ Base
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-5 text-sm text-white/80 italic border-l-2 border-[#00A651] pl-4">
            {g.ours_note}
          </p>
          <p className="mt-4 text-xs text-white/40 leading-relaxed">{g.footer}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PriceComparisonDialog;
